import { useEffect, useState } from 'react';

import { Api } from '@core/api';
import { injectTSDI } from '@core/tsdi';
import { CdnObjectDto, ImageType } from '@api/ChallengeClient';

interface UseImageUploadResult {
    loading: boolean;
    error: string | undefined;
    previewUrl: string | undefined;
    handleUpload: (file: File) => Promise<void>;
}

/**
 * Wraps imageSupportControllerUpload + a local preview URL for use in form
 * stores. The caller passes an `onUploaded` callback that receives the
 * resulting CdnObjectDto so it can write into its own form fields.
 *
 * Blob preview URLs are revoked on replacement and on unmount.
 */
export function useImageUpload(
    imageType: ImageType,
    onUploaded: (cdn: CdnObjectDto) => void
): UseImageUploadResult {
    const { challengeClient } = injectTSDI(Api);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | undefined>();
    const [previewUrl, setPreviewUrl] = useState<string | undefined>();

    useEffect(() => {
        return () => {
            if (previewUrl) {
                URL.revokeObjectURL(previewUrl);
            }
        };
    }, [previewUrl]);

    const handleUpload = async (file: File) => {
        setLoading(true);
        setError(undefined);

        try {
            const result =
                await challengeClient.support.imageSupportControllerUpload(
                    { type: imageType },
                    { file }
                );

            onUploaded(result.data);

            if (previewUrl) {
                URL.revokeObjectURL(previewUrl);
            }
            setPreviewUrl(URL.createObjectURL(file));
        } catch (err) {
            console.error('Image upload failed:', err);
            setError('Failed to upload image. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return { loading, error, previewUrl, handleUpload };
}
